import { Form, Badge, Button, ProgressBar } from 'react-bootstrap';
import { SessionType, GenderType } from '../../models';
import styles from './SessionSetup.module.css';

export function InfoRow({
  session,
  type,
  setType,
  squadCount,
  setSquadCount,
  alternating,
  setAlternating,
  byePositions,
  setByePositions,
  autoAssignSquads,
  setAutoAssignSquads,
  autoUpdateTitle,
  setAutoUpdateTitle,
  progress,
  syncing,
  diagnosing,
  errorMessage,
  onConfigureAll,
}) {
  // Show bye position selectors for women's format with byes:
  // - 5 squads (1 bye, 5 columns)
  // - 6 squads (2 byes, 6 columns)
  // - alternating 3 squads (2 byes, 6 columns)
  const isAlternatingTri = alternating && squadCount === 3;
  const showByePositionSelector =
    type === SessionType.MULTI &&
    (session.gender === GenderType.FEMALE
      ? squadCount === 5 ||
        squadCount === 6 ||
        squadCount === 7 ||
        isAlternatingTri
      : squadCount === 7);

  const handleByePositionChange = (index, newPos) => {
    const newPositions = [...byePositions];
    newPositions[index] = parseInt(newPos, 10);
    setByePositions(newPositions);
  };

  // Number of columns and byes depends on configuration
  const numColumns = isAlternatingTri ? 6 : squadCount;
  const isMale = session.gender === GenderType.MALE;
  const numByes = isMale ? 1 : squadCount === 5 ? 1 : squadCount === 7 ? 3 : 2;
  return (
    <div className={styles.infoRow}>
      <div className={styles.genderBadge}>
        <Badge variant="secondary">
          {session.gender === GenderType.FEMALE ? 'Women' : 'Men'}
        </Badge>
      </div>
      <Form.Control
        as="select"
        value={type}
        onChange={(e) => setType(e.target.value)}
        className={styles.infoSelect}
      >
        {Object.values(SessionType).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </Form.Control>
      {type === SessionType.MULTI && (
        <Form.Control
          type="number"
          min={1}
          max={12}
          value={squadCount}
          onChange={(e) => setSquadCount(parseInt(e.target.value, 10))}
          className={styles.infoSquadInput}
          title="Number of squads"
        />
      )}
      <Form.Check
        type="switch"
        id="alt-switch"
        label="Alternating"
        checked={alternating}
        onChange={(e) => setAlternating(e.target.checked)}
        className={styles.infoSwitch}
      />
      {showByePositionSelector && (
        <>
          <span className={styles.byeLabel}>Bye:</span>
          <Form.Control
            as="select"
            value={byePositions[0]}
            onChange={(e) => handleByePositionChange(0, e.target.value)}
            className={styles.byePositionSelect}
            title="First bye position"
          >
            {Array.from({ length: numColumns }, (_, i) => i + 1)
              .filter(
                (p) =>
                  (numByes < 2 || p !== byePositions[1]) &&
                  (numByes < 3 || p !== byePositions[2])
              )
              .map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
          </Form.Control>
          {numByes >= 2 && (
            <>
              <span className={styles.byeAnd}>&</span>
              <Form.Control
                as="select"
                value={byePositions[1]}
                onChange={(e) => handleByePositionChange(1, e.target.value)}
                className={styles.byePositionSelect}
                title="Second bye position"
              >
                {Array.from({ length: numColumns }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p !== byePositions[0] &&
                      (numByes < 3 || p !== byePositions[2])
                  )
                  .map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
              </Form.Control>
            </>
          )}
          {numByes >= 3 && (
            <>
              <span className={styles.byeAnd}>&</span>
              <Form.Control
                as="select"
                value={byePositions[2]}
                onChange={(e) => handleByePositionChange(2, e.target.value)}
                className={styles.byePositionSelect}
                title="Third bye position"
              >
                {Array.from({ length: numColumns }, (_, i) => i + 1)
                  .filter((p) => p !== byePositions[0] && p !== byePositions[1])
                  .map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
              </Form.Control>
            </>
          )}
        </>
      )}
      <Form.Check
        type="switch"
        id="auto-assign-switch"
        label="Auto-Assign"
        checked={autoAssignSquads}
        onChange={(e) => setAutoAssignSquads(e.target.checked)}
        className={styles.infoSwitch}
      />
      <Form.Check
        type="switch"
        id="auto-title-switch"
        label="Update Title"
        checked={autoUpdateTitle}
        onChange={(e) => setAutoUpdateTitle(e.target.checked)}
        className={styles.infoSwitch}
      />

      {/* Spacer to push progress and button to the right */}
      <div style={{ flex: 1 }} />

      {/* Progress and Configure button */}
      <div className={styles.progressSection}>
        <div className={styles.progressLabel}>
          <span>Progress</span>
          <span>{progress.overall}%</span>
        </div>
        <ProgressBar
          now={progress.overall}
          variant={progress.overall === 100 ? 'success' : 'primary'}
          className={styles.progressBar}
        />
      </div>
      <Button
        variant="primary"
        disabled={syncing || diagnosing || !!errorMessage}
        onClick={onConfigureAll}
        className={styles.configureButton}
      >
        {diagnosing
          ? 'Diagnosing...'
          : syncing
          ? 'Applying...'
          : 'Configure All'}
      </Button>
    </div>
  );
}
